import React, {useEffect} from 'react'
import Image from '@components/atoms/Image'
import Button from '../../utils/components/atoms/Button'

interface Prize {
  date: string
  layout: string
  max_width: string
  heading: string
  description: string
  accent_title?: string
  accent_title_bg?: string
  tags?: string[]
  cta_text?: string
  cta_link?: string
  cta_variant?: string
  image_desktop?: string
  image_mobile?: string
  image_alt?: string
}

export interface TurboPrizeData {
  placeholder_title?: string
  placeholder_description?: string
  prizes: Prize[]
}

const FALLBACK_IMAGE_PLACEHOLDER = `
  <div class="flex h-full w-full flex-col items-center justify-center px-5 py-10 text-center text-black lg:px-12 lg:py-[94px]">
    <p class="mb-2 block text-[120px] leading-[120px] lg:mb-8 lg:text-[180px] lg:leading-[180px]">⚠️</p>
    <p class="mb-3.5 text-[18px] leading-[22px]"><strong>404: Prize Not Found (Yet)</strong></p>
    <p class="mx-auto w-full max-w-[340px] text-[16px] leading-[18px] lg:text-[18px] lg:leading-[22px]">Our prize servers are currently doing a keg stand. The next drop goes live in 24 hours, assuming they survive.</p>
  </div>
` as const

const MOUNTAIN_TIME_ZONE = 'America/Denver'
const MOUNTAIN_FALLBACK_OFFSET = -7 * 60 // minutes

const getFormattedPartsForZone = (date: Date, timeZone: string): Intl.DateTimeFormatPart[] => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'shortOffset',
  })

  return formatter.formatToParts(date)
}

const getOffsetMinutes = (parts: Intl.DateTimeFormatPart[]): number => {
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  const match = timeZonePart.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/)

  if (!match) {
    return MOUNTAIN_FALLBACK_OFFSET
  }

  const sign = match[1].startsWith('-') ? -1 : 1
  const hours = Math.abs(parseInt(match[1], 10))
  const minutes = match[2] ? parseInt(match[2], 10) : 0

  return sign * (hours * 60 + minutes)
}

const getCurrentTimeInZone = (timeZone: string): number => {
  const now = new Date()
  const parts = getFormattedPartsForZone(now, timeZone)
  const year = parseInt(parts.find((part) => part.type === 'year')?.value ?? '0', 10)
  const month = parseInt(parts.find((part) => part.type === 'month')?.value ?? '1', 10) - 1
  const day = parseInt(parts.find((part) => part.type === 'day')?.value ?? '1', 10)
  const hour = parseInt(parts.find((part) => part.type === 'hour')?.value ?? '0', 10)
  const minute = parseInt(parts.find((part) => part.type === 'minute')?.value ?? '0', 10)
  const second = parseInt(parts.find((part) => part.type === 'second')?.value ?? '0', 10)
  const offsetMinutes = getOffsetMinutes(parts)
  const utcMilliseconds = Date.UTC(year, month, day, hour, minute, second)

  return utcMilliseconds - offsetMinutes * 60 * 1000
}

const normaliseDateString = (date: string): number | null => {
  if (!date) {
    return null
  }

  const trimmedDate = date.trim()
  const hasTime = trimmedDate.includes('T')
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(trimmedDate)
  let normalisedDateString = trimmedDate

  if (!hasTime) {
    normalisedDateString = `${normalisedDateString}T00:00:00`
  }

  if (!hasOffset) {
    normalisedDateString = `${normalisedDateString}-07:00`
  }

  const parsedDate = new Date(normalisedDateString)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate.getTime()
}


const TurboPrize: React.FC<TurboPrizeData> = ({
  prizes,
  placeholder_title,
  placeholder_description
}) => {
  const nowInMountainMs = getCurrentTimeInZone(MOUNTAIN_TIME_ZONE)
  const releaseTimestamps = prizes.map(({ date }) => {
    const trimmedDate = typeof date === 'string' ? date.trim() : date
    return normaliseDateString(trimmedDate)
  })
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, []);

  const nextUpcomingReleaseTimestamp = releaseTimestamps.reduce<number | null>((nearest, timestamp) => {
    if (timestamp === null || timestamp <= nowInMountainMs) {
      return nearest
    }

    if (nearest === null || timestamp < nearest) {
      return timestamp
    }

    return nearest
  }, null)
  const placeholderImageMarkup = FALLBACK_IMAGE_PLACEHOLDER
  const placeholderImageLabel = 'day_bro-be-patient.png'

  return (
    <div className="w-full">
      {prizes.map(
        (
          {
            layout,
            max_width,
            heading,
            description,
            accent_title,
            accent_title_bg,
            tags,
            cta_text,
            cta_link,
            cta_variant,
            image_desktop,
            image_alt,
          },
          index,
        ) => {
          const key = `${heading || placeholder_title || 'prize'}-${index}`
          const releaseTimestamp = releaseTimestamps[index] ?? null
          const isNearestUpcoming = nextUpcomingReleaseTimestamp !== null && releaseTimestamp === nextUpcomingReleaseTimestamp
          const headingStyle: React.CSSProperties = {
            maxWidth: max_width ? `${max_width}px` : '100%',
          }
          const accentTitleStyle: React.CSSProperties | undefined = accent_title_bg
            ? {
                background: accent_title_bg,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }
            : undefined

          const isUnlocked = releaseTimestamp === null ? true : nowInMountainMs >= releaseTimestamp || isNearestUpcoming
          const isPastDue = releaseTimestamp !== null && nowInMountainMs > releaseTimestamp

          const effectiveHeading = isUnlocked ? heading : placeholder_title || heading
          const effectiveDescription = isUnlocked ? description : placeholder_description || description
          const shouldRenderCta = Boolean(isUnlocked && !isPastDue && cta_text)
          const imageBarLabel = isUnlocked && image_desktop ? image_alt : placeholderImageLabel
          const formattedTags = (tags || []).map((tag) => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean)
          const prizeId = image_alt?.split('.')[0];

          return (
            <div
              key={key}
              className="mb-14 grid grid-cols-1 gap-10 px-5 lg:mb-20 lg:grid-cols-2 lg:gap-14 lg:px-0"
              id={prizeId}
            >
              <div className="pt-2 [&>.button]:w-full lg:[&>.button]:w-auto">
                {formattedTags.length ? (
                  <div className="flex items-start justify-start mb-3">
                    {formattedTags.map((tag, i) => (
                      <span
                        key={`tag-${i}`}
                        className="prize-tag-bg mr-2 flex h-[23px] min-w-[70px] items-center justify-center text-center font-jetBrainsMono text-[11px] leading-[16px] text-black"
                      >
                        {tag.toUpperCase()}
                      </span>
                    ))}
                  </div>
                ) : null}

                {accent_title ? (
                  <h4
                    className="mb-4 font-orbitron text-[48px] leading-[52px] tracking-[-1.92px]"
                    style={accentTitleStyle}
                  >
                    {accent_title}
                  </h4>
                ) : null}

                {effectiveHeading ? (
                  <h3
                    className="mb-7 font-appleGaramond text-[64px] font-light tracking-[-2.56px] leading-[52px] lg:text-[96px] lg:tracking-[-3.84px] lg:leading-[78px]"
                    style={headingStyle}
                  >
                    {effectiveHeading}
                  </h3>
                ) : null}

                {effectiveDescription ? (
                  <div
                    className="text-[21px] leading-[26px] [&>p]:mb-7 [&>p>em]:text-[18px]"
                    dangerouslySetInnerHTML={{ __html: effectiveDescription }}
                  />
                ) : null}

                {shouldRenderCta ? (
                  <Button
                    mode="link"
                    className={cta_variant}
                    buttonText={cta_text as string}
                    url={cta_link}
                  />
                ) : null}
              </div>

              <div
                className={[
                  'flex flex-col items-center order-first',
                  layout === 'image-text' ? 'lg:order-first' : 'lg:order-none',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="relative flex w-full items-center rounded-tl-[4px] rounded-tr-[4px] bg-[linear-gradient(180deg,#D0D0D0_0%,#A6A6A6_100%)] p-0.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.40),inset_0_-1px_0_0_#000]">
                  <div className="flex items-center absolute left-2">
                    <div className="prize-image-ellipse mr-1 h-3.5 w-3.5 overflow-hidden rounded-full" />
                    <div className="prize-image-ellipse mr-1 h-3.5 w-3.5 overflow-hidden rounded-full" />
                    <div className="prize-image-ellipse mr-1 h-3.5 w-3.5 overflow-hidden rounded-full" />
                  </div>
                  <p className="w-full px-14 text-center text-[12px] leading-[14px] text-black">{imageBarLabel}</p>
                </div>

                <div className="w-full rounded-bl-[4px] rounded-br-[4px] bg-white p-3">
                  <div className="h-full w-full overflow-hidden rounded-[3px] border border-solid border-[rgba(0,0,0,0.08)] bg-[#EDEDED] shadow-[0_2px_2px_0_rgba(0,0,0,0.15)_inset]">
                    {isUnlocked && image_desktop ? (
                      <Image
                        alt={image_alt ? image_alt : 'day_bro-be-patient.png'}
                        src={image_desktop}
                        srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
                        height={800}
                        width={640}
                        classes="block w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-full w-full items-center justify-center bg-[#EDEDED]"
                        dangerouslySetInnerHTML={{ __html: placeholderImageMarkup }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        },
      )}
    </div>
  )
}

export default TurboPrize
